import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { apiClient } from '../publicApiClient';
import { toUrlFriendly, fromUrlFriendlyCity } from '../utils/urlUtils';
import { citySegmentFromSearchPath } from '../utils/searchPaths';

function cityFromSearchPath(pathname: string, cities: string[]): string | null {
  const citySegment = citySegmentFromSearchPath(pathname);
  if (!citySegment) {
    return null;
  }
  return fromUrlFriendlyCity(citySegment, cities);
}

const CityFilter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cities, setCities] = useState<string[]>(['Warszawa']);

  const getInitialCity = () => {
    return cityFromSearchPath(location.pathname, []) ?? 'Warszawa';
  };

  const [selectedCity, setSelectedCity] = useState<string>(getInitialCity());

  useEffect(() => {
    const properCityName = cityFromSearchPath(location.pathname, cities);
    if (properCityName) {
      setSelectedCity(properCityName);
    }
  }, [location.pathname, cities]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesList = await apiClient.getCities();
        const citiesWithWarszawa = citiesList.includes('Warszawa')
          ? citiesList
          : ['Warszawa', ...citiesList];
        setCities(citiesWithWarszawa);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
        setCities(['Warszawa']);
      }
    };
    fetchCities();
  }, []);

  const handleCityChange = (event: SelectChangeEvent) => {
    const newCity = event.target.value;
    setSelectedCity(newCity);

    const newUrl = `/user/search/${toUrlFriendly(newCity)}`;

    sessionStorage.setItem('cityFilterNavigating', 'true');
    sessionStorage.setItem('cityFilterTarget', newUrl);

    navigate(newUrl, { replace: true });

    setTimeout(() => {
      const finalPath = window.location.pathname;

      if (finalPath !== newUrl && finalPath === '/user/search') {
        const fullUrl = window.location.origin + newUrl;
        window.history.replaceState({}, '', fullUrl);
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
      }

      sessionStorage.removeItem('cityFilterNavigating');
      sessionStorage.removeItem('cityFilterTarget');
    }, 100);
  };

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: 120, sm: 150 },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'background.paper',
          borderRadius: 2,
          '& fieldset': {
            borderColor: 'primary.main',
          },
          '&:hover fieldset': {
            borderColor: 'primary.dark',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.dark',
            borderWidth: 2,
          },
        },
        '& .MuiSelect-select': {
          color: 'text.primary',
          padding: { xs: '6px 8px', sm: '8px 12px' },
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        },
        '& .MuiInputLabel-root': {
          color: 'text.secondary',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          '&.Mui-focused': {
            color: 'primary.main',
          },
        },
      }}
    >
      <Select
        labelId="city-filter-label"
        id="city-filter"
        value={selectedCity}
        onChange={handleCityChange}
        startAdornment={<LocationOnIcon sx={{ color: 'primary.main', fontSize: '1rem', mr: 0.5 }} />}
      >
        {cities.map((cityName) => (
          <MenuItem key={cityName} value={cityName}>
            {cityName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CityFilter;
